/* global React */
const { useState, useEffect, useRef, useMemo } = React;

// ─── Content (ES / EN) ─────────────────────────────────────────────────────
const CONTENT = {
  es: {
    nav: { work: "Proyectos", bio: "Sobre mí", community: "Comunidad UXUI", contact: "Contratame" },
    splash: {
      tagline: { pre: "armando la UI", accent: "ferpecta", post: ", para tu experiencia" },
      progress: "ensamblando",
    },
    home: {
      heroEyebrow: "last update 2026.05.19",
      heroTitle: { pre: "Anita, ", accent: "diseñadora", post: " de soluciones" },
      heroBajada: {
        neutral: "+10 años haciendo UX · Fundadora de Comunidad UXUI",
        recruiter: "15 años tomando decisiones de diseño que afectan productos reales. Acá los casos que conviene mostrarle a tu cliente.",
        founder: "El criterio que necesitás en tu equipo, sin la estructura que no podés pagar. Pasamos un rato pensando tu producto.",
        designer: "Acá vas a encontrar cómo pienso, cómo decido y cómo llegué hasta acá. Sin filtro de portfolio.",
      },
      heroMeta: {
        based: "Argentina · trabajando con equipos globales",
        community: "Solucionadora de problemas",
      },
      sections: {
        projects: { tag: "01 · trabajo seleccionado", title: "Proyectos" },
        bio: { tag: "02 · sobre mí", title: "Cómo llegué hasta acá" },
        community: { tag: "03 · Comunidad UXUI", title: "" },
        reviews: { tag: "04 · reviews", title: "Lo que dicen" },
        metrics: { tag: "05 · meta", title: "Métricas del portfolio" },
      },
      bentoLabels: { result: "resultado", role: "rol" },
      bio: {
        photoAlt: "Anita Sevilla Ull, retrato — sonriendo, pelo largo oscuro, remera verde clara, fondo de ventana abierta con vegetación.",
        // La última palabra del último párrafo se pinta en handwriting accent.
        // Se busca al cierre del último ledePara — debe estar seguida de "." y final.
        accentWord: "personas",
        // Extracto del home — la historia completa vive en la página About.
        ledeParas: [
          "Empecé estudiando psicología. Después cine. Después marketing. Hoy diseño productos digitales y fundé la comunidad de UX más grande en español.",
          "El hilo conductor siempre fue el mismo: entender cómo piensan las personas.",
        ],
        chips: [
          "Diseño de comportamiento",
          "IXD",
          "Solucionadora de problemas",
          "IA early adopter",
          "Crypto fan",
        ],
        cta: "Conoce más de mí",
        ctaSecondary: "O conocéme en 90s →",
        // Se mantiene el bloque por perfil en el code base por si se quiere
        // recuperar; el home actual ya no lo renderiza (extracto plano).
        byProfile: {
          neutral: {
            tag: "Vista neutra · disciplinas",
            kind: "spec",
            items: [
              { l: "Research", v: "Generativa, evaluativa, de campo" },
              { l: "Interacción", v: "Productos densos, dashboards, herramientas" },
              { l: "UX/UI", v: "Generalista con criterio" },
            ],
          },
          recruiter: {
            tag: "Vista reclutador · herramientas, artefactos, tipo de trabajo",
            kind: "skills",
            groups: [
              { l: "Skills", items: ["Research", "Interaction Design", "UI Systems", "Design Leading"] },
              { l: "Herramientas", items: ["Figma", "Notion", "Maze", "Dovetail", "Mixpanel", "FigJam"] },
              { l: "Artefactos", items: ["Audit UX", "Journey map", "Guías de entrevista", "Design system", "Prototipo testeable"] },
              { l: "Tipo de trabajo", items: ["Full-time remoto", "Contract trimestral", "Sprint de 2 semanas"] },
            ],
          },
          founder: {
            tag: "Vista founder · impacto medido",
            kind: "stats",
            items: [
              { v: "+38%", l: "retención · DJY Pro" },
              { v: "−24%", l: "drop-off · onboarding fintech" },
              { v: "12x", l: "ciclo de research · AI tool" },
              { v: "+18%", l: "on-time · last-mile" },
              { v: "12.4k", l: "comunidad LATAM · 5 años" },
              { v: "320+", l: "matches de mentoría" },
            ],
          },
          designer: {
            tag: "Vista diseñador · cómo pienso",
            kind: "process",
            steps: [
              { t: "Entender antes que dibujar", d: "Research que orienta, no que decora. Si no cambia una decisión, no se hace." },
              { t: "Decidir en voz alta", d: "Cada interacción tiene un por qué que se puede defender ante PM, ingeniería y negocio." },
              { t: "UI al servicio", d: "La UI no compite con la función. Densidad cuando hace falta, aire cuando aclara." },
              { t: "Cerrar con evidencia", d: "Lo que no se mide, se asume. Y lo que se asume, suele estar mal." },
            ],
          },
        },
      },
      about: {
        navChip: "p · 02 · sobre mí",
        apertura: {
          line1: { text: "Empecé estudiando la mente humana.", handWord: "mente" },
          line2: { text: "Hoy diseño sistemas que la entienden.", handWord: "sistemas" },
        },
        sections: [
          {
            tag: "// QUIÉN SOY",
            paragraphs: [
              { text: "No llegué al diseño de producto por el diseño." },
              { text: "Llegué por las personas.", emphasis: true },
              { text: "Antes de diseñar interfaces, estudié psicología. Aprendí coaching ontológico, sociología, neurofisiología, lenguaje corporal. Estudié cómo Paul Ekman lee microexpresiones. Trabajé en una peluquería donde, en cada color, entendía a alguien diferente — sus miedos, sus expectativas, por qué decían una cosa, pero hacían otra.", strongAt: "por qué decían una cosa, pero hacían otra" },
              { text: "Después hice cine. Me recibí en Guión y dirección. Aprendí a construir relatos que mueven a la gente, a entender qué hace que alguien siga mirando o deje de hacerlo. Y como productora entendí que cada segundo vale dinero, que si llego a tiempo estoy tarde, y que las necesidades del resto las tengo que conocer — y cubrir — antes que ellos mismos.", strongAt: "entendí que cada segundo vale dinero, que si llego a tiempo estoy tarde" },
              { text: "Después me gradué también en marketing y publicidad. Aprendí a escalar relatos, a hacer que lleguen a muchos y que vendan." },
              { text: "Y en algún punto de ese camino, alguien me puso delante un producto digital con usuarios que no lo entendían — y supe exactamente qué estaba pasando y qué había que hacer.", strongAt: "supe exactamente qué estaba pasando y qué había que hacer" },
              { text: "Eso fue hace 10 años…ya llevo 10 años diseñando productos digitales." },
            ],
          },
          {
            tag: "// CÓMO TRABAJO",
            paragraphs: [
              { text: "No juego con soluciones únicas. Presento opciones fundamentadas y defino con criterio.", strongAt: "Presento opciones fundamentadas y defino con criterio." },
              { text: "Trabajo mejor en equipos que necesitan a alguien que piense, decida y comunique con claridad — no solo que ejecute.", strongAt: "piense, decida y comunique con claridad — no solo que ejecute" },
              { text: "He liderado equipos de más de 20 personas y he trabajado sola con un desarrollador. Los dos escenarios me gustan. En equipos grandes, orquesto. En equipos chicos, me ensucio las manos.", strongAt: "En equipos grandes, orquesto. En equipos chicos, me ensucio las manos." },
              { text: "Soy rápida. Me engancho profundo y rápido con los problemas — y me aburro igual de rápido cuando el proyecto ya no presenta desafíos. Por eso en donde más me dejo el alma son los proyectos que tienen impacto real y feedback constante." },
              { text: "Mi proceso siempre empieza entendiendo qué quieren las personas — todas las personas: los usuarios, la gente de negocio, los de desarrollo, mi equipo…todas las personas." },
              { text: "No solo lo que dicen que quieren, sino lo que realmente necesitan. Hay una diferencia.", strongAt: "lo que realmente necesitan" },
            ],
          },
          {
            tag: "// LA COMUNIDAD",
            paragraphs: [
              { text: "En 2021 dando clases, vi que los diseñadores UX no tenían un lugar propio. No una academia. O un mero canal de YouTube. Un lugar donde crecer en comunidad, donde equivocarse sin miedo y aprender con pares, con criterio." },
              { text: "Lo fundé. Hoy es la comunidad de UX más grande en español — más de 40K de seguidores en 18 países y miles de miembros activos.", strongAt: "la comunidad de UX más grande en español" },
              { text: "No lo digo para hacer número. Lo digo porque fundarlo me obligó a ser diseñadora, educadora, estratega de contenido, product manager y emprendedora al mismo tiempo. Usar todos esos sombreros cambió definitivamente la forma en que resuelvo problemas de negocio.", strongAt: "Usar todos esos sombreros cambió definitivamente la forma en que resuelvo problemas de negocio." },
            ],
          },
          {
            tag: "// LO QUE NO VERÁS EN MI CV",
            paragraphs: [
              { text: "Vivo en la selva misionera con mi marido y tres perros. Me mudé lejos del ruido a propósito." },
              { text: "Juego a Catan, al póker y al ajedrez — y aunque lo parezca, no es un dato menor: me gustan los juegos donde leer al otro es parte de la estrategia.", strongAt: "me gustan los juegos donde leer al otro es parte de la estrategia." },
              { text: "Cuando crypto era una rareza, compré. (Todavía tengo esos satoshis y al mirar mis billeteras pienso 'HODL!')" },
              { text: "Cuando ChatGPT salió, estuve el primer día. No por FOMO — porque genuinamente me interesa entender las herramientas antes de que todo el mundo las use." },
              { text: "Estoy casada con un front-end architect. Llevamos 14 años viviendo juntos y eso me enseñó más sobre comunicación con desarrollo que cualquier proceso de handoff." },
            ],
            pullQuote: { text: "Entender de verdad a una persona — cómo piensa, qué la mueve, dónde está el nervio — es la habilidad más valiosa que tengo.", handWord: "habilidad" },
            closingPara: "Me llevo bien con todo el mundo, pero me caen mejor quienes miran a los ojos, dan la mano con firmeza y hablan con valor.",
          },
          {
            tag: "// LO QUE ME FORMÓ",
            intro:
              "Estos son algunos de los libros que más han impactado en mi realidad diaria, en mi forma de entender y navegar el mundo:",
            kind: "books",
            books: [
              { title: "El mundo de Sofía", author: "Jostein Gaarder" },
              { title: "El comportamiento del consumidor", author: "Leon G. Schiffman · Leslie Lazar Kanuk" },
              { title: "$100M Offers", author: "Alex Hormozi" },
              { title: "El diseño de las cosas de todos los días", author: "Don Norman" },
              { title: "El rostro de las emociones", author: "Paul Ekman" },
              { title: "Rodeado de idiotas", author: "Thomas Erikson" },
              { title: "El caballero de la armadura oxidada", author: "Robert Fisher" },
              { title: "Espejo rojo: Nuestro futuro se escribe en China", author: "Simone Pieranni" },
              { title: "Artificial", author: "Mariano Sigman y Sebastián Bilinkis" },
              { title: "Nexus", author: "Yuval Noah Harari" },
            ],
            // Bloque de cierre/CTA después de los libros — cierra la página
            // con un argumento de venta + acción única (Contratame).
            outro: {
              paragraphs: [
                {
                  variant: "sans",
                  lines: [
                    "No leo para acumular datos, leo para encontrar patrones.",
                    "Todo lo que aprendí sobre comportamiento, negocios y tecnología hoy lo traduzco en interfaces que funcionan y escalan.",
                  ],
                },
                {
                  variant: "mono",
                  lines: [
                    "Si como diseñador necesitas ayuda personalizada para mejorar tus procesos, conseguir un aumento o nuevo puesto;",
                    "Si tu proyecto o equipo necesita a alguien que sepa conectar el modelo de negocio con la psicología del usuario,",
                    {
                      text: "En fin… si quieres trabajar con alguien que no tenga miedo de ensuciarse las manos hasta lograr el objetivo, es por acá.",
                      strongAt: "si quieres trabajar con alguien que no tenga miedo de ensuciarse las manos hasta lograr el objetivo",
                    },
                  ],
                },
              ],
              cta: { label: "Contratame →", href: "mailto:anabelensevillaull@gmail.com" },
              ctaSecondary: { label: "Descargar CV ↓", href: "assets/cv/Anita_Sevilla_Ull-UX_Lead-Product_Designer-ES.pdf", download: "Anita_Sevilla_Ull-UX_Lead-Product_Designer.pdf" },
            },
          },
        ],
        foot: {
          ctaPrimary: "Contratame →",
          ctaSecondary: "Volver al portfolio",
        },
      },
      community: {
        tag: "Fundadora desde 2021",
        title: "Comunidad UXUI",
        logoAlt: "Comunidad UXUI — logo",
        srTitle: "Comunidad UXUI",
        sub: "La comunidad de UX más grande del mundo hispanohablante. Donde los diseñadores aumentan su seniority a un ritmo acelerado.",
        stats: [
          { v: "+40K", l: "Seguidores" },
          { v: "+300", l: "CUXUItos activos" },
          { v: "18", l: "Países" },
          { v: "5 años", l: "Construyendo" },
        ],
        cta: "Ir a CUXUI",
        ctaHref: "https://www.skool.com/comunidaduxui/about",
      },
      metrics: {
        items: [
          { l: "Visitas totales", v: 0, target: 14380, sub: "últimos 30 días", bars: 12 },
          { l: "Perfil más frecuente", v: "Founder", sub: "42% del tráfico" },
          { l: "Caso más visto", v: "DJY Pro", sub: "Dow Jones + Yomiuri" },
          { l: "Tráfico por país", v: "AR · MX · ES", sub: "+ 12 países" },
        ],
      },
      naturalDesborde: {
        tag: "p / s · desborde natural",
        title: "Antes de irte,",
        body: "Llegaste hasta el final sin abrir el panel lateral. Te dejo acá el video corto donde te cuento, en 90 segundos, cómo trabajo y con quién me conviene trabajar.",
        cta: "Reproducir video →",
        framelabel: "VSL · 9:16 · 1:32",
      },
      footer: {
        coda: "Hecho con criterio en Buenos Aires · 2026",
        sig: "Anita",
        cols: [
          { h: "Encuéntrame", links: [
            { label: "LinkedIn", href: "https://www.linkedin.com/in/anitasevillaull-ux/", external: true },
            { label: "Comunidad UXUI", href: "https://www.skool.com/comunidaduxui/about", external: true },
            { label: "Mentoría · ADPList", href: "https://adplist.org/mentors/anita-sevilla-ull", external: true },
            { label: "Descargar CV ↓", href: "assets/cv/Anita_Sevilla_Ull-UX_Lead-Product_Designer-ES.pdf", download: "Anita_Sevilla_Ull-UX_Lead-Product_Designer.pdf" },
          ] },
          { h: "Repo", links: [
            { label: "Articles · Medium", href: "https://medium.com/@anitasevillaull", external: true },
            { label: "Figma", href: "https://www.figma.com/@anita_ux", external: true },
            { label: "Behance", href: "https://www.behance.net/anitasevillaull", external: true },
          ] },
        ],
        socials: [
          { name: "LinkedIn", icon: "linkedin", href: "https://www.linkedin.com/in/anitasevillaull-ux/" },
          { name: "YouTube", icon: "youtube", href: "https://www.youtube.com/@ComunidadUXUI" },
          { name: "Medium", icon: "medium", href: "https://medium.com/@anitasevillaull" },
        ],
        ctaPrimary: { label: "Contratame →", href: "mailto:anabelensevillaull@gmail.com" },
      },
    },
    desborde: {
      tabLabel: "Conocé a Anita · 1:32",
      vsl: {
        tag: "VSL · 9:16",
        title: "Conocé a Anita en 90s",
        body: "Sin filtro de portfolio. Cómo trabajo, con quién me conviene trabajar, y qué evito.",
        cta: "Agendar una llamada →",
      },
      reviews: { tag: "Reviews", title: "Lo que dicen quienes me contrataron" },
      asideAriaVsl: "Video de presentación",
      asideAriaReviews: "Reviews",
      caseDjyAsideAria: "Reviews del equipo DJY Pro",
      caseDjyTabLabel: "Lo que dijo el equipo · DJY",
    },
    breadcrumb: { ariaLabel: "Migas de pan", home: "Home" },
    reviewLabels: { lead: "Líder", team: "Equipo", mentee: "Mentoría" },
  },
  en: {
    nav: { work: "Projects", bio: "About", community: "UXUI Community", contact: "Hire me" },
    splash: {
      tagline: { pre: "building the ", accent: "ferpect", post: " UI for your experience" },
      progress: "assembling",
    },
    home: {
      heroEyebrow: "last update 2026.05.19",
      heroTitle: { pre: "Anita, solutions ", accent: "designer", post: "" },
      heroBajada: {
        neutral: "+10 years doing UX · Founder of UXUI Community",
        recruiter: "15 years making design decisions that move real products. Here are the cases worth showing your client.",
        founder: "The judgment you need on your team, without the overhead you can't afford. Let's spend a moment thinking about your product.",
        designer: "Here's how I think, how I decide and how I got here. No portfolio filter.",
      },
      heroMeta: {
        based: "Argentina · working with global teams",
        community: "Problem solver",
      },
      sections: {
        projects: { tag: "01 · selected work", title: "Projects" },
        bio: { tag: "02 · about", title: "How I got here" },
        community: { tag: "03 · UXUI Community", title: "" },
        reviews: { tag: "04 · reviews", title: "What people say" },
        metrics: { tag: "05 · meta", title: "Portfolio metrics" },
      },
      bentoLabels: { result: "result", role: "role" },
      bio: {
        photoAlt: "Anita Sevilla Ull, portrait — smiling, long dark hair, light green tee, open window with greenery in the background.",
        accentWord: "people",
        ledeParas: [
          "I started studying psychology. Then film. Then marketing. Today I design digital products and I founded the largest Spanish-speaking UX community.",
          "The thread was always the same: understanding how people think.",
        ],
        chips: [
          "Behavioral design",
          "IxD",
          "Problem solver",
          "AI early adopter",
          "Crypto fan",
        ],
        cta: "Get to know me better",
        ctaSecondary: "Or meet me in 90s →",
        byProfile: {
          neutral: {
            tag: "Neutral view · disciplines",
            kind: "spec",
            items: [
              { l: "Research", v: "Generative, evaluative, in-field" },
              { l: "Interaction", v: "Dense products, dashboards, tools" },
              { l: "UX/UI", v: "Generalist with a point of view" },
            ],
          },
          recruiter: {
            tag: "Recruiter view · tools, artifacts, work type",
            kind: "skills",
            groups: [
              { l: "Skills", items: ["Research", "Interaction Design", "UI Systems", "Design Leading"] },
              { l: "Tools", items: ["Figma", "Notion", "Maze", "Dovetail", "Mixpanel", "FigJam"] },
              { l: "Artifacts", items: ["UX audit", "Journey map", "Interview guides", "Design system", "Testable prototype"] },
              { l: "Work type", items: ["Full-time remote", "Quarterly contract", "2-week sprint"] },
            ],
          },
          founder: {
            tag: "Founder view · measured impact",
            kind: "stats",
            items: [
              { v: "+38%", l: "retention · DJY Pro" },
              { v: "−24%", l: "drop-off · fintech onboarding" },
              { v: "12x", l: "research cycle · AI tool" },
              { v: "+18%", l: "on-time · last-mile" },
              { v: "12.4k", l: "LATAM community · 5 yrs" },
              { v: "320+", l: "mentor matches" },
            ],
          },
          designer: {
            tag: "Designer view · how I think",
            kind: "process",
            steps: [
              { t: "Understand before drawing", d: "Research that orients, doesn't decorate. If it doesn't change a decision, skip it." },
              { t: "Decide out loud", d: "Every interaction has a why you can defend to PM, engineering and the business." },
              { t: "UI in service", d: "UI doesn't fight the function. Density when it's needed, air when it clarifies." },
              { t: "Close with evidence", d: "What you don't measure, you assume. And what you assume is usually wrong." },
            ],
          },
        },
      },
      about: {
        navChip: "p · 02 · about",
        apertura: {
          line1: { text: "I started by studying the human mind.", handWord: "mind" },
          line2: { text: "Today I design systems that understand it.", handWord: "systems" },
        },
        sections: [
          {
            tag: "// WHO I AM",
            paragraphs: [
              { text: "I didn't get into product design through design." },
              { text: "I got there through people.", emphasis: true },
              { text: "Before I designed interfaces, I studied psychology. I learned ontological coaching, sociology, neurophysiology, body language. I studied how Paul Ekman reads microexpressions. I worked at a hair salon where every color appointment was a different person to read — their fears, their expectations, why they said one thing and did another.", strongAt: "why they said one thing and did another" },
              { text: "Then I did film. I graduated in Screenwriting and Direction. I learned to build stories that move people, to understand what makes someone keep watching or look away. As a producer I learned that every second costs money, that if I'm on time I'm late, and that I have to know — and cover — other people's needs before they do.", strongAt: "I learned that every second costs money, that if I'm on time I'm late" },
              { text: "Then I also got a degree in marketing and advertising. I learned how to scale stories — how to make them reach many people and sell." },
              { text: "And at some point along that path, someone put a digital product in front of me with users who didn't get it — and I knew exactly what was going on and what to do about it.", strongAt: "I knew exactly what was going on and what to do about it" },
              { text: "That was 10 years ago…I've been designing digital products for 10 years now." },
            ],
          },
          {
            tag: "// HOW I WORK",
            paragraphs: [
              { text: "I don't play with single solutions. I present grounded options and decide with judgment.", strongAt: "I present grounded options and decide with judgment." },
              { text: "I work best with teams that need someone who thinks, decides and communicates clearly — not just someone who executes.", strongAt: "thinks, decides and communicates clearly — not just someone who executes" },
              { text: "I've led teams of 20+ people and I've worked alone with a single developer. I like both. On big teams I orchestrate. On small teams I get my hands dirty.", strongAt: "On big teams I orchestrate. On small teams I get my hands dirty." },
              { text: "I'm fast. I get hooked deep and fast on problems — and I get bored just as fast when the project stops being a challenge. That's why I leave it all on projects with real impact and constant feedback." },
              { text: "My process always starts by understanding what people want — all the people: users, business, dev, my team…everyone." },
              { text: "Not just what they say they want, but what they actually need. There's a difference.", strongAt: "what they actually need" },
            ],
          },
          {
            tag: "// THE COMMUNITY",
            paragraphs: [
              { text: "In 2021, while teaching, I saw that UX designers didn't have a place of their own. Not an academy. Not just another YouTube channel. A place to grow as a community, mess up without fear, and learn from peers with real judgment." },
              { text: "I built it. Today it's the largest Spanish-speaking UX community — 40K+ followers across 18 countries and thousands of active members.", strongAt: "the largest Spanish-speaking UX community" },
              { text: "I don't say it to brag. I say it because founding it forced me to be a designer, an educator, a content strategist, a product manager and an entrepreneur at the same time. Wearing all those hats permanently changed the way I solve business problems.", strongAt: "Wearing all those hats permanently changed the way I solve business problems." },
            ],
          },
          {
            tag: "// WHAT YOU WON'T FIND ON MY CV",
            paragraphs: [
              { text: "I live in the Misiones jungle (NE Argentina) with my husband and three dogs. I moved far from the noise on purpose." },
              { text: "I play Catan, poker and chess — and that's not a small detail: I like games where reading the other person is part of the strategy.", strongAt: "I like games where reading the other person is part of the strategy." },
              { text: "When crypto was a curiosity, I bought. (I still have those satoshis and when I check my wallets I think 'HODL!')" },
              { text: "When ChatGPT launched, I was there on day one. Not FOMO — I genuinely care about understanding tools before everyone uses them." },
              { text: "I'm married to a front-end architect. We've lived together for 14 years and that taught me more about communicating with engineering than any handoff process ever has." },
            ],
            pullQuote: { text: "Really understanding a person — how they think, what moves them, where the nerve is — is the most valuable skill I have.", handWord: "skill" },
            closingPara: "I get along with everyone, but the people I really click with look you in the eye, give a firm handshake, and speak with courage.",
          },
          {
            tag: "// WHAT SHAPED ME",
            intro:
              "Some of the books that have shaped how I see and move through the world the most:",
            kind: "books",
            books: [
              { title: "Sophie's World", author: "Jostein Gaarder" },
              { title: "Consumer Behavior", author: "Leon G. Schiffman · Leslie Lazar Kanuk" },
              { title: "$100M Offers", author: "Alex Hormozi" },
              { title: "The Design of Everyday Things", author: "Don Norman" },
              { title: "Emotions Revealed", author: "Paul Ekman" },
              { title: "Surrounded by Idiots", author: "Thomas Erikson" },
              { title: "The Knight in Rusty Armor", author: "Robert Fisher" },
              { title: "Red Mirror: Our Future is Written in China", author: "Simone Pieranni" },
              { title: "Artificial", author: "Mariano Sigman & Sebastián Bilinkis" },
              { title: "Nexus", author: "Yuval Noah Harari" },
            ],
            outro: {
              paragraphs: [
                {
                  variant: "sans",
                  lines: [
                    "I don't read to collect facts. I read to find patterns.",
                    "Everything I've learned about behavior, business and technology — today I translate it into interfaces that work and scale.",
                  ],
                },
                {
                  variant: "mono",
                  lines: [
                    "If you're a designer who needs personalized help to sharpen your process, land a raise or a new role;",
                    "If your project or team needs someone who knows how to connect the business model to user psychology,",
                    {
                      text: "In short… if you want to work with someone who isn't afraid to get their hands dirty until the goal is met, it's this way.",
                      strongAt: "if you want to work with someone who isn't afraid to get their hands dirty until the goal is met",
                    },
                  ],
                },
              ],
              cta: { label: "Hire me →", href: "mailto:anabelensevillaull@gmail.com" },
              ctaSecondary: { label: "Download CV ↓", href: "assets/cv/Anita_Sevilla_Ull-Product_Designer-EN.pdf", download: "Anita_Sevilla_Ull-Product_Designer.pdf" },
            },
          },
        ],
        foot: {
          ctaPrimary: "Hire me →",
          ctaSecondary: "Back to portfolio",
        },
      },
      community: {
        tag: "Founder since 2021",
        title: "UXUI Community",
        logoAlt: "UXUI Community — logo",
        srTitle: "UXUI Community",
        sub: "The largest UX community in the Spanish-speaking world. Where designers level up faster.",
        stats: [
          { v: "+40K", l: "Followers" },
          { v: "+300", l: "Active CUXUItos" },
          { v: "18", l: "Countries" },
          { v: "5 yrs", l: "Building" },
        ],
        cta: "Go to CUXUI",
        ctaHref: "https://www.skool.com/comunidaduxui/about",
      },
      metrics: {
        items: [
          { l: "Total visits", v: 0, target: 14380, sub: "last 30 days", bars: 12 },
          { l: "Most frequent profile", v: "Founder", sub: "42% of traffic" },
          { l: "Most viewed case", v: "DJY Pro", sub: "Dow Jones + Yomiuri" },
          { l: "Traffic by country", v: "AR · MX · ES", sub: "+ 12 countries" },
        ],
      },
      naturalDesborde: {
        tag: "p / s · natural overflow",
        title: "Before you go,",
        body: "You made it to the end without opening the side panel. Here's the short video where I tell you, in 90 seconds, how I work and who's the right fit.",
        cta: "Play video →",
        framelabel: "VSL · 9:16 · 1:32",
      },
      footer: {
        coda: "Made with judgment in Buenos Aires · 2026",
        sig: "Anita",
        cols: [
          { h: "Find me", links: [
            { label: "LinkedIn", href: "https://www.linkedin.com/in/anitasevillaull-ux/", external: true },
            { label: "UXUI Community", href: "https://www.skool.com/comunidaduxui/about", external: true },
            { label: "Mentoring · ADPList", href: "https://adplist.org/mentors/anita-sevilla-ull", external: true },
            { label: "Download CV ↓", href: "assets/cv/Anita_Sevilla_Ull-Product_Designer-EN.pdf", download: "Anita_Sevilla_Ull-Product_Designer.pdf" },
          ] },
          { h: "Repo", links: [
            { label: "Articles · Medium", href: "https://medium.com/@anitasevillaull", external: true },
            { label: "Figma", href: "https://www.figma.com/@anita_ux", external: true },
            { label: "Behance", href: "https://www.behance.net/anitasevillaull", external: true },
          ] },
        ],
        socials: [
          { name: "LinkedIn", icon: "linkedin", href: "https://www.linkedin.com/in/anitasevillaull-ux/" },
          { name: "YouTube", icon: "youtube", href: "https://www.youtube.com/@ComunidadUXUI" },
          { name: "Medium", icon: "medium", href: "https://medium.com/@anitasevillaull" },
        ],
        ctaPrimary: { label: "Hire me →", href: "mailto:anabelensevillaull@gmail.com" },
      },
    },
    desborde: {
      tabLabel: "Meet Anita · 1:32",
      vsl: {
        tag: "VSL · 9:16",
        title: "Meet Anita in 90s",
        body: "No portfolio filter. How I work, who I work best with, what I avoid.",
        cta: "Book a call →",
      },
      reviews: { tag: "Reviews", title: "What the people who hired me say" },
      asideAriaVsl: "Intro video",
      asideAriaReviews: "Reviews",
      caseDjyAsideAria: "DJY Pro team reviews",
      caseDjyTabLabel: "What the team said · DJY",
    },
    breadcrumb: { ariaLabel: "Breadcrumb", home: "Home" },
    reviewLabels: { lead: "Lead", team: "Team", mentee: "Mentoring" },
  },
};

// project bank — featured slot changes per profile
// Lang-aware: each translatable field is { es, en }.
const PROJECTS = [
  {
    id: "djy",
    title: { es: "DJY Pro — Dow Jones + Yomiuri", en: "DJY Pro — Dow Jones + Yomiuri" },
    industry: { es: "Finance · News intelligence", en: "Finance · News intelligence" },
    role: { es: "UX Lead", en: "UX Lead" },
    year: "2024",
    result: { es: "78% de retención", en: "78% retention" },
    tone: "oklch(0.92 0.03 60)",
    description: { es: "Dashboard personalizable desde 0 para culturas opuestas.", en: "Modular dashboard built from zero for two opposite cultures." },
  },
  {
    id: "ai",
    title: { es: "De chatbot a agente", en: "From chatbot to agent" },
    industry: { es: "AdTech · IA + SaaS", en: "AdTech · AI + SaaS" },
    role: { es: "Interactional Designer", en: "Interactional Designer" },
    year: "2026",
    result: { es: "Agente proactivo", en: "Proactive agent" },
    tone: "oklch(0.92 0.03 195)",
    description: { es: "Creación de identidad, voz y comportamiento.", en: "Identity, voice and behavior, built from the ground up." },
  },
];

// Reviews — text/role como { es, en }. El label se deriva de
// CONTENT[lang].reviewLabels[category] en lugar de venir hardcoded.
const REVIEWS_BANK = [
  // ── Jefes / leads que me contrataron ────────────────────────────────
  {
    category: "lead",
    name: "Cristian Muñoz",
    role: { es: "Director of Innovation", en: "Director of Innovation" },
    text: {
      es: "Gracias por tu dedicación y esfuerzo. Me gusta mucho que desde el inicio hayas impactado desde tu perspectiva en todo.",
      en: "Thank you for your dedication and effort. I really like that from the start you've had an impact, from your own perspective, on everything.",
    },
  },
  {
    category: "lead",
    name: "Jamilton Alonso Quintero Osorio",
    role: { es: "Founder & CTO", en: "Founder & CTO" },
    text: {
      es: "La velocidad con la que entregaste tanta calidad me impresionó. Aportaste mucho valor y fuiste un gran impulso para que el equipo sacara algo muy importante en tiempo récord.",
      en: "The speed at which you delivered so much quality impressed me. You brought a lot of value and you were a major push for the team to ship something really important in record time.",
    },
  },
  {
    category: "lead",
    name: "Jawahar Desai",
    role: { es: "Director of Product Management, B2B Products · Dow Jones", en: "Director of Product Management, B2B Products · Dow Jones" },
    text: {
      es: "¡Muchísimas gracias por todas tus enormes contribuciones al lanzamiento de DJYPro! Te vamos a extrañar mucho — fue un genuino placer colaborar con vos.",
      en: "Thank you so much for all your immense contributions to the launch of DJYPro! You will be greatly missed — it was a genuine pleasure collaborating with you.",
    },
  },
  {
    category: "lead",
    name: "Tim White",
    role: { es: "Senior Product Director · Dow Jones", en: "Senior Product Director · Dow Jones" },
    text: {
      es: "Tu aporte fue invaluable. Una verdadera jugadora de equipo, y alguien siempre dispuesta a encontrar puntos en común.",
      en: "Your input was invaluable. A true team player, and someone always willing to find common ground.",
    },
  },
  // ── Colegas / equipo ────────────────────────────────────────────────
  {
    category: "team",
    name: "Jesús Reyna González",
    role: { es: "Sr. UI/UX — equipo en el mismo proyecto", en: "Sr. UI/UX — on the same project team" },
    text: {
      es: "Ana es una persona muy inteligente, con pasión por su trabajo en UX/UI. Aprendí mucho con ella sobre tests de usabilidad y sobre cómo hacer las preguntas correctas para conseguir resultados efectivos. Es muy organizada y me ayudó a serlo también, siempre con la mejor disposición.",
      en: "Ana is a very intelligent person who has passion for her work in UX/UI. I learned a lot from her about usability tests and make the right questions to get effective results. She is very organized and had help me being too, always willing to help.",
    },
  },
  {
    category: "team",
    name: "Estefania Zucar Nuñez",
    role: { es: "Learning Quality Specialist — me reportaba directo", en: "Learning Quality Specialist — direct report" },
    text: {
      es: "Trabajé con Anita en dos proyectos, y anteriormente hice el curso de Diseño UX/UI con ella en Coderhouse. Es muy eficaz a la hora de comunicar objetivos y delegar. En las experiencias que compartimos, siempre estuvo para resolver dudas y aportar información de calidad. Aparte de tener muchísima experiencia como diseñadora UX/UI, es una Lead increíble 🙌.",
      en: "I worked with Anita on two projects, and before that I took her UX/UI Design course at Coderhouse. She's very effective at communicating goals and delegating. In every experience we shared, she was there to answer questions and bring high-quality information to the table. On top of having tons of experience as a UX/UI designer, she's an incredible Lead 🙌.",
    },
  },
  // ── Mentees / mentorías ─────────────────────────────────────────────
  {
    category: "mentee",
    name: "Marcell Simon",
    role: { es: "UX/UI Designer · Budapest Business School", en: "UX/UI Designer · Budapest Business School" },
    text: {
      es: "Anita no solo revisa tu portfolio — desafía la forma en que pensás tu carrera. Su feedback es agudo, honesto y está anclado en experiencia real de la industria. Identifica rápido qué es lo que realmente te bloquea y te deja con puntos claros sobre los que reflexionar.",
      en: "Anita doesn't just review your portfolio — she challenges how you think about your career. Her feedback is sharp, honest, and grounded in real industry experience. She quickly identifies what actually blocks progress and leaves you with clear points to reflect on.",
    },
  },
  {
    category: "mentee",
    name: "Pavel Pan",
    role: { es: "UX Designer · Humber Polytechnic", en: "UX Designer · Humber Polytechnic" },
    text: {
      es: "¡Tuve una experiencia excepcional con Anita! Sus habilidades de comunicación, fuera de serie, hicieron que la sesión fuera muy enganchadora, y su amplio conocimiento en UX, marketing y psicología aportó insights invaluables. Genuinamente me motivó a explorar nuevas ideas y enfoques en diseño del comportamiento. Recomiendo muchísimo reservar una sesión con ella.",
      en: "I had an outstanding experience with Anita! Her exceptional communication skills made our session engaging, and her extensive knowledge in UX, marketing, and psychology provided invaluable insights. She genuinely motivated me to explore new ideas and approaches in behavioral design. I highly recommend booking a session with her.",
    },
  },
  {
    category: "mentee",
    name: "Nicolas Quinteros",
    role: { es: "UX/UI Designer · Aulica", en: "UX/UI Designer · Aulica" },
    text: {
      es: "Me supo hacer las preguntas correctas para conocer mi flujo actual de trabajo, me ayudó a ver qué técnicas de análisis implementar en mi situación actual, me ayudó a visualizar que el comportamiento de los usuarios no siempre es el mismo a la forma en que piensan, me inspiró a mirar el mundo y cómo las personas interactúan con él.",
      en: "She knew how to ask me the right questions to understand my current workflow, she helped me see which analysis techniques to use in my situation, she helped me visualize that user behavior isn't always the same as the way users think, and she inspired me to look at the world and how people interact with it.",
    },
  },
];

window.CONTENT = CONTENT;
window.PROJECTS = PROJECTS;
window.REVIEWS_BANK = REVIEWS_BANK;
